import React, { useState, useEffect } from 'react';
import { Cookie, Gem, Headphones, Leaf, Package, Shirt } from 'lucide-react';
import StepHeading from './StepHeading';
import { noAutofill } from '../noAutofill';

interface Step2Props {
  category: string;
  setCategory: (category: string) => void;
  handleStep2: (customDescription?: string) => void;
  loading: boolean;
  categories: string[];
  productShortDescription: string;
  setProductShortDescription: (productShortDescription: string) => void;
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  "Women's Fashion": <Gem />,
  "Men's Fashion": <Shirt />,
  Electronics: <Headphones />,
  'Packaged Food': <Cookie />,
  'Health & Wellness': <Leaf />,
};

const PLACEHOLDERS: Record<string, string> = {
  'Health & Wellness': 'Vitamin C immune support capsules with zinc',
  "Women's Fashion": 'Flowy summer maxi dress with a hand-drawn floral print',
  "Men's Fashion": 'Classic slim-fit denim jeans with four-way stretch',
  Electronics: 'Wireless noise-cancelling headphones with 5-minute quick charge',
  'Packaged Food': 'Organic frozen pizza on a cauliflower crust',
};

const Step2: React.FC<Step2Props> = ({
  category,
  setCategory,
  handleStep2,
  loading,
  categories,
  productShortDescription,
  setProductShortDescription,
}) => {
  const [draft, setDraft] = useState(productShortDescription);

  useEffect(() => {
    setProductShortDescription(draft);
  }, [draft, setProductShortDescription]);

  return (
    <div className="ed-enter">
      <StepHeading
        index={2}
        kicker="The Brief"
        title="What are we making?"
        lede="Choose a category, then describe the product in a sentence. The more specific you are, the sharper everything downstream becomes."
      />

      <div className="ed-block">
        <span className="ed-kicker">Category</span>

        <div className="ed-tiles" role="radiogroup" aria-label="Product category">
          {categories.map((cat) => {
            const isSelected = category === cat;
            return (
              <div
                key={cat}
                role="radio"
                aria-checked={isSelected}
                tabIndex={0}
                className={`ed-tile${isSelected ? ' is-selected' : ''}`}
                onClick={() => {
                  setCategory(cat);
                  setDraft('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setCategory(cat);
                    setDraft('');
                  }
                }}
              >
                <span className="ed-tile-icon" aria-hidden="true">
                  {CATEGORY_ICONS[cat] || <Package />}
                </span>
                <span className="ed-tile-label">{cat}</span>
              </div>
            );
          })}
        </div>
      </div>

      {category && (
        <div className="ed-block ed-block--sheet ed-enter">
          <label className="ed-label" htmlFor="product-brief">
            Describe it
          </label>
          <input
            id="product-brief"
            type="text"
            className="ed-input ed-input--display"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && draft.trim() && !loading) {
                e.preventDefault();
                handleStep2(draft);
              }
            }}
            placeholder={PLACEHOLDERS[category] || 'Describe your product…'}
            {...noAutofill}
          />
          <p className="ed-hint">One vivid sentence works better than a paragraph.</p>
        </div>
      )}
    </div>
  );
};

export default Step2;
